'use client';

import { useDebouncedEffect } from '@react-hookz/web';
import { Loader2, RefreshCw, SearchIcon } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { ModelPlaceholder } from './ModelPlaceholder';
import { OptimizedScenarioImage } from './OptimizedScenarioImage';
import type { ScenarioImageModel, ScenarioModel, ScenarioModel3D } from '@/lib/scenario';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';
type ModelType = ScenarioModel | ScenarioImageModel | ScenarioModel3D;
type SortOption = 'default' | 'newest' | 'oldest' | 'name';

/** Capability filter option with human-readable label */
export interface CapabilityFilterOption {
  value: string;
  label: string;
}
interface GenericModelSelectorProps<T extends ModelType> {
  selectedModel: T | null;
  onModelSelect: (model: T) => void;
  disabled?: boolean;
  label: string;
  dialogTitle: string;
  dialogDescription: string;
  loadModels: (options?: {
    forceRefresh?: boolean;
  }) => Promise<T[]>;
  placeholder?: string;
  showFeaturedBadge?: boolean;
  /** Capability filter options - if provided, shows a filter dropdown */
  capabilityFilters?: CapabilityFilterOption[];
}

/** Generic model selector component with modal dialog */
export const GenericModelSelector = <T extends ModelType,>({
  selectedModel,
  onModelSelect,
  disabled,
  label,
  dialogTitle,
  dialogDescription,
  loadModels,
  placeholder = 'Select a model',
  showFeaturedBadge = false,
  capabilityFilters
}: GenericModelSelectorProps<T>) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [models, setModels] = useState<T[]>([]);
  const [isLoadingModels, setIsLoadingModels] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  useDebouncedEffect(() => setDebouncedSearchQuery(searchQuery), [searchQuery], 150);
  const [sortOption, setSortOption] = useState<SortOption>('default');
  const [selectedCapability, setSelectedCapability] = useState<string>('all');
  const [selectedTag, setSelectedTag] = useState<string>('all');

  // Hovered model for info panel
  const [hoveredModel, setHoveredModel] = useState<T | null>(null);
  const loadModelsData = async (forceRefresh = false) => {
    setIsLoadingModels(true);
    try {
      const loadedModels = await loadModels({
        forceRefresh
      });
      setModels(loadedModels);
    } catch (error) {
      console.error('Failed to load models:', error);
      setModels([]);
    } finally {
      setIsLoadingModels(false);
    }
  };

  /** Count models per capability for dropdown labels */
  const capabilityCounts = useMemo(() => {
    if (!capabilityFilters) return {};
    const counts: Record<string, number> = {};
    for (const filter of capabilityFilters) {
      counts[filter.value] = models.filter(model => {
        const caps = 'capabilities' in model ? (model as any).capabilities ?? [] : [];
        return caps.includes(filter.value);
      }).length;
    }
    return counts;
  }, [models, capabilityFilters]);

  /** Extract unique tags from all models for tag filter */
  const availableTags = useMemo((): string[] => {
    const tagSet = new Set<string>();
    for (const model_0 of models) {
      if ('tags' in model_0 && model_0.tags) {
        for (const tag of model_0.tags) {
          if (!tag.startsWith('sc:')) tagSet.add(tag); // Exclude system tags
        }
      }
    }
    return Array.from(tagSet).sort();
  }, [models]);
  const filteredAndSortedModels = useMemo(() => {
    let result = [...models];

    // Filter by capability (single selection)
    if (selectedCapability !== 'all') {
      result = result.filter(model_1 => {
        const caps_0 = 'capabilities' in model_1 ? (model_1 as any).capabilities ?? [] : [];
        return caps_0.includes(selectedCapability);
      });
    }

    // Filter by selected tag
    if (selectedTag !== 'all') {
      result = result.filter(model_2 => {
        const modelTags = 'tags' in model_2 ? model_2.tags ?? [] : [];
        return modelTags.includes(selectedTag);
      });
    }

    // Filter by search query (debounced)
    if (debouncedSearchQuery.trim()) {
      const query = debouncedSearchQuery.toLowerCase();
      result = result.filter(model_3 => model_3.name.toLowerCase().includes(query) || 'description' in model_3 && model_3.description?.toLowerCase().includes(query) || 'tags' in model_3 && model_3.tags?.some(tag_0 => tag_0.toLowerCase().includes(query)));
    }

    // Sort (featured always first, then apply sort)
    const isFeatured = (m: T) => 'tags' in m && (m.tags?.includes('sc:featured') || 'featured' in m && m.featured);
    const getDate = (m_0: T) => 'updatedAt' in m_0 ? new Date((m_0 as any).updatedAt).getTime() : 0;
    result.sort((a, b) => {
      // Featured always first
      const aFeatured = isFeatured(a);
      const bFeatured = isFeatured(b);
      if (aFeatured && !bFeatured) return -1;
      if (!aFeatured && bFeatured) return 1;

      // Then apply sort option (default preserves original order)
      if (sortOption === 'default') return 0;
      if (sortOption === 'newest') return getDate(b) - getDate(a);
      if (sortOption === 'oldest') return getDate(a) - getDate(b);
      if (sortOption === 'name') return a.name.localeCompare(b.name);
      return 0;
    });
    return result;
  }, [models, debouncedSearchQuery, sortOption, selectedCapability, selectedTag]);
  const handleOpenModal = async () => {
    if (disabled) return;
    setIsModalOpen(true);
    if (models.length > 0) return; // Already loaded
    await loadModelsData();
  };
  const handleRefresh = async () => {
    await loadModelsData(true);
  };
  const handleSelectModel = (model_4: T) => {
    onModelSelect(model_4);
    setIsModalOpen(false);
  };
  const getModelImage = (model_5: T): string | undefined => {
    if ('images' in model_5 && model_5.images?.[0]) {
      return model_5.images[0].url;
    }
    if ('thumbnail' in model_5 && model_5.thumbnail?.url) {
      return model_5.thumbnail.url;
    }
    return undefined;
  };
  const getModelDescription = (model_6: T): string | undefined => {
    return 'description' in model_6 ? model_6.description : undefined;
  };

  /** Handle grid mouse move - find closest card using event delegation */
  const handleGridMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const target = (e.target as HTMLElement).closest('[data-model-index]') as HTMLElement | null;
    if (target) {
      const index = parseInt(target.dataset.modelIndex!, 10);
      const model_7 = filteredAndSortedModels[index];
      if (model_7 && model_7 !== hoveredModel) setHoveredModel(model_7);
    }
  }, [filteredAndSortedModels, hoveredModel]);
  const handleGridMouseLeave = useCallback(() => {
    setHoveredModel(null);
  }, []);
  const hoveredModelTags = hoveredModel && 'tags' in hoveredModel ? hoveredModel.tags : [];
  const hoveredModelDescription = hoveredModel ? getModelDescription(hoveredModel) : undefined;
  const hoveredModelType = hoveredModel && 'type' in hoveredModel ? (hoveredModel as any).type : undefined;
  const hoveredModelCreatedAt = hoveredModel && 'createdAt' in hoveredModel ? new Date((hoveredModel as any).createdAt).toLocaleDateString() : undefined;
  const hoveredModelUpdatedAt = hoveredModel && 'updatedAt' in hoveredModel ? new Date((hoveredModel as any).updatedAt).toLocaleDateString() : hoveredModel && 'lastUpdatedDate' in hoveredModel ? new Date((hoveredModel as any).lastUpdatedDate).toLocaleDateString() : undefined;
  return <>
      <div className="space-y-2">
        <Button onClick={handleOpenModal} disabled={disabled} className="w-full justify-between h-auto p-3">
          <div className="flex items-center space-x-3">
            {selectedModel && (getModelImage(selectedModel) ? <OptimizedScenarioImage src={getModelImage(selectedModel)!} alt={selectedModel.name} aspectRatio="auto" className="w-10 h-10 rounded" /> : <ModelPlaceholder modelName={selectedModel.name} tags={selectedModel.tags} className="w-10 h-10 rounded" compact />)}
            <div className="text-left space-y-0.5">
              {selectedModel && <Label className="text-[10px] font-medium">{label}</Label>}
              <div className="font-semibold text-sm">
                {selectedModel?.name ?? placeholder}
              </div>
            </div>
          </div>
        </Button>
      </div>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="sm:max-w-6xl h-[70vh] flex flex-col" onOpenAutoFocus={e_0 => e_0.preventDefault()}>
          <DialogTitle>{dialogTitle}</DialogTitle>
          <div className="flex items-start justify-between gap-2">
            <DialogDescription className="flex-1">
              {dialogDescription}
            </DialogDescription>
            <Button onClick={handleRefresh} disabled={isLoadingModels} size="icon" variant="default" tooltip="Refresh models" className="h-auto py-1">
              {isLoadingModels ? <Loader2 className="size-3.5 animate-spin" /> : <RefreshCw className="size-3.5" />}
            </Button>
          </div>

          {/* Search, Filter, and Sort Controls */}
          <div className="flex items-center gap-2 shrink-0">
            <div className="relative flex-1 min-w-[200px]">
              <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
              <Input type="search" placeholder="Search models..." value={searchQuery} onChange={e_1 => setSearchQuery(e_1.target.value)} className="pl-8 h-9" />
            </div>
            {availableTags.length > 0 && <Select value={selectedTag} onValueChange={setSelectedTag}>
                <SelectTrigger className="w-[180px] h-9 bg-background">
                  <SelectValue placeholder="All Tags" />
                </SelectTrigger>
                <SelectContent className="bg-popover max-h-[300px]">
                  <SelectItem value="all">All Tags</SelectItem>
                  {availableTags.map(tag_1 => <SelectItem key={tag_1} value={tag_1}>{tag_1}</SelectItem>)}
                </SelectContent>
              </Select>}
            {capabilityFilters && capabilityFilters.length > 0 && <Select value={selectedCapability} onValueChange={setSelectedCapability}>
                <SelectTrigger className="w-[180px] h-9 bg-background">
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent className="bg-popover">
                  <SelectItem value="all">All Types ({models.length})</SelectItem>
                  {capabilityFilters.map(filter_0 => <SelectItem key={filter_0.value} value={filter_0.value}>
                      {filter_0.label} ({capabilityCounts[filter_0.value] ?? 0})
                    </SelectItem>)}
                </SelectContent>
              </Select>}
            <Select value={sortOption} onValueChange={v => setSortOption(v as SortOption)}>
              <SelectTrigger className="w-[140px] h-9 bg-background">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent className="bg-popover">
                <SelectItem value="default">Sort Default</SelectItem>
                <SelectItem value="newest">Newest First</SelectItem>
                <SelectItem value="oldest">Oldest First</SelectItem>
                <SelectItem value="name">Name A-Z</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Grid - uses event delegation for hover detection */}
          <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-0 flex-1 min-h-0 overflow-y-auto pr-2 content-start mt-3" onMouseMove={handleGridMouseMove} onMouseLeave={handleGridMouseLeave}>
            {isLoadingModels ? (/* Skeleton grid while loading */
          Array.from({
            length: 18
          }).map((_, i) => <div key={i} className="p-1.5">
                  <div className="border-2 rounded-lg p-1.5 border-muted">
                    <div className="aspect-square bg-muted animate-pulse rounded-md" />
                    <div className="h-4 bg-muted animate-pulse rounded w-3/4 mt-1" />
                    <div className="flex flex-wrap gap-0.5 mt-1 h-[38px]">
                      <div className="h-[17px] w-10 bg-muted animate-pulse rounded" />
                      <div className="h-[17px] w-8 bg-muted animate-pulse rounded" />
                    </div>
                  </div>
                </div>)) : filteredAndSortedModels.map((model_8, index_0) => {
            const imageUrl = getModelImage(model_8);
            const isFeatured_0 = 'tags' in model_8 && model_8.tags?.includes('sc:featured');
            const isSelected = selectedModel?.id === model_8.id;
            const modelTags_0 = 'tags' in model_8 ? model_8.tags : [];
            return <div key={`${model_8.id}-${index_0}`} data-model-index={index_0} className="p-1.5 cursor-pointer group" onClick={() => handleSelectModel(model_8)}>
                    <div className={cn('border-2 rounded-lg p-1.5 group-hover:bg-accent transition-colors h-full', isSelected ? 'border-primary bg-primary/5' : 'border-muted group-hover:border-muted-foreground/20')}>
                      <div className="relative overflow-hidden rounded-md">
                        {imageUrl ? <OptimizedScenarioImage src={imageUrl} alt={model_8.name} aspectRatio="square" className="rounded-md" groupHover /> : <ModelPlaceholder modelName={model_8.name} tags={model_8.tags} className="aspect-square rounded-md" />}
                        {showFeaturedBadge && isFeatured_0 && <div className="absolute top-1.5 right-1.5 bg-white text-black text-[9px] font-semibold px-2 py-0.5 rounded-md shadow-md">
                            Featured
                          </div>}
                      </div>
                      <h4 className="font-medium text-xs line-clamp-1 mt-1.5">{model_8.name}</h4>
                      <div className="flex flex-wrap gap-1 mt-1.5 h-[44px] overflow-hidden content-start">
                        {modelTags_0?.slice(0, 4).map((tag_2: string) => <span key={tag_2} className="px-1.5 py-0.5 bg-muted text-[10px] text-foreground rounded-full border-0 border-border/50 h-fit">
                            {tag_2}
                          </span>)}
                      </div>
                    </div>
                  </div>;
          })}
          </div>

          {/* Fixed info panel at bottom */}
          <div className="min-h-20 mt-2 px-3 py-2 bg-muted/50 rounded-lg flex items-start gap-3 border border-border/50 shrink-0 relative">
            {/* Empty state - fades out when model is hovered */}
            <div className={cn('absolute inset-0 flex items-center px-3 transition-opacity duration-200', hoveredModel ? 'opacity-0 pointer-events-none' : 'opacity-100')}>
              <span className="text-xs text-muted-foreground">Hover over a model to see details</span>
            </div>
            {/* Hovered model content - fades in */}
            <div className={cn('flex items-start gap-3 w-full transition-all duration-200', hoveredModel ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-1')}>
              {hoveredModel && (getModelImage(hoveredModel) ? <OptimizedScenarioImage src={getModelImage(hoveredModel)!} alt={hoveredModel.name} aspectRatio="square" className="size-16 rounded shrink-0" /> : <ModelPlaceholder modelName={hoveredModel.name} tags={hoveredModel.tags} className="size-16 rounded shrink-0" />)}
              {hoveredModel && <div className="flex-1 min-w-0 space-y-1">
                  <div className="flex items-center gap-2">
                    <div className="font-medium text-sm truncate">{hoveredModel.name}</div>
                    {hoveredModelType && <span className="px-1.5 py-0.5 bg-primary/10 text-primary text-xs font-medium rounded">{hoveredModelType}</span>}
                    <div className="flex items-center gap-2 ml-auto shrink-0 text-xs text-muted-foreground">
                      {hoveredModelCreatedAt && <span>Created: {hoveredModelCreatedAt}</span>}
                      {hoveredModelUpdatedAt && <span>Updated: {hoveredModelUpdatedAt}</span>}
                    </div>
                  </div>
                  {hoveredModelDescription && <div className="text-sm text-foreground">{hoveredModelDescription}</div>}
                  {hoveredModelTags && hoveredModelTags.length > 0 && <div className="flex items-center gap-1">
                      <span className="text-xs text-muted-foreground">Tags:</span>
                      <span className="text-xs text-muted-foreground">{hoveredModelTags.slice(0, 6).join(', ')}{hoveredModelTags.length > 6 ? ` +${hoveredModelTags.length - 6} more` : ''}</span>
                    </div>}
                </div>}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>;
};
GenericModelSelector.displayName = 'GenericModelSelector';